// Angular imports
import { Component, OnInit, OnDestroy, HostListener, ElementRef, ViewChild } from '@angular/core';
import { trigger, state, transition, animate, style } from '@angular/animations';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { Title } from '@angular/platform-browser';

// Import rxjs
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

// Import config
import { ConfigService } from '../config';

// Import modal service and modals
import { ModalService } from '../modal';
import { ChangelogModalComponent } from '../modals';

// Import side menu service
import { SideMenuService } from '../services/side-menu.service';

// Import services
import {
    ChangesService,
    ExportService, ExportType
} from '../services';
import { Store } from '@ngrx/store';
import { AppState } from '../state-management/app-state';
import { selectCalculationMeta, selectSideMenuOpen } from '../state-management/calculation-selectors';
import * as CalculationActions from '../state-management/calculation-actions';
import { PdfExportComponent } from '../modals/pdf-export/pdf-export.component';

@Component({
    selector: 'side-menu',
    templateUrl: './side-menu.component.html',
    styleUrls: ['./side-menu.component.scss'],
    animations: [
        trigger('side-menu', [
            state('closed', style({
                left: '-29ch'
            })),
            state('open', style({
                left: '0'
            })),
            transition('closed <=> open', [
                animate('.15s ease-in-out')
            ])
        ])
    ],
    standalone: false
})
export class SideMenuComponent implements OnInit, OnDestroy {
    @ViewChild('root') public rootElement: ElementRef<HTMLDivElement>;
    private unsubscribe$ = new Subject<void>();

    public _open: boolean = true;
    public _selectedItem: number = 1;
    public _hasActiveCalculation: boolean = false;
    public _calculationOpened: boolean = false;
    public _version: string = '';

    constructor(private title: Title,
        private router: Router,
        private location: Location,
        private config: ConfigService,
        // private sideMenuService: SideMenuService,
        private changesService: ChangesService,
        private modalService: ModalService,
        private exportService: ExportService,
        private store: Store
    ) { }

    public ngOnInit(): void {
        // Get the version from the config
        this._version = this.config.version;

        // Handle active calculation change event
        this.store.select(selectCalculationMeta).pipe(
            takeUntil(this.unsubscribe$)
        )
            .subscribe(meta => {
                this._hasActiveCalculation = meta !== undefined;
            })

        this.store.select(selectSideMenuOpen).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(isOpen => {
            this._open = isOpen;

            if(!isOpen) {
                this._selectedItem = null;
            }
        });

        this.router.events.pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(event => {
            if (!(event instanceof NavigationEnd)) return;
            // console.log('event', event);
            

            if (/^\/project\/[\d\*]+\/calc\/[a-z0-9\-]*$/.test(event.url)) {
                this.store.dispatch(CalculationActions.hideSideMenu());
                this._calculationOpened = true;
                this._selectedItem = null;
            } else{
                this._calculationOpened = false;
                this.store.dispatch(CalculationActions.showSideMenu())
            }
        })
    }

    public ngOnDestroy(): void {
        // Unsubscribe
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public _close(): void {
        // console.log('close side menu');
        // Cannot close if there is no active calculation
        if (!this._hasActiveCalculation || !this._calculationOpened) return;

        // Close the side menu
        this.store.dispatch(CalculationActions.hideSideMenu())
        // this.sideMenuService.close();
    }

    public _selectOpenProject(): void {
        // Set selected item
        this._selectedItem = 1;

        // No project
        // this.applicationDataService.unsetProject();

        // Update the title
        this.title.setTitle('Calculatie');

        // Navigate to base url
        this.router.navigate(['']);
    }

    /* --- Layout --- */

    public _selectDefaultLayout(): void {
        this.store.dispatch(CalculationActions.updateTableLayout({ layout: 'default' }));
        this._close();
    }

    public _selectCondesedLayout(): void {
        this.store.dispatch(CalculationActions.updateTableLayout({ layout: 'condensed' }));
        this._close();
    }

    /* --- Exports --- */

    public _exportAsExcel(): void {
        this.exportService.exportExcel();
        this._close();
    }

    public _exportAsCostExcel(): void {
        this.exportService.exportCostExcel();
        this._close();
    }

    public _exportAsCuf(): void {
        this.exportService.exportCuf();
        this._close();
    }

    public _exportAsBudget(): void {
        this.exportService.exportPdf(ExportType.budget);
        this._close();
    }

    public _exportAsQuotation(): void {
        this.exportService.exportPdf(ExportType.quotation);
        this._close();
    }

    public _exportAsHours(): void {
        this.exportService.exportPdf(ExportType.hours);
        this._close();
    }

    public _exportAsNet(): void {
        this.exportService.exportPdf(ExportType.net);
        this._close();
    }

    public _exportAsGross(): void {
        this.exportService.exportPdf(ExportType.gross);
        this._close();
    }

    public _exportAsQuantities(): void {
        this.exportService.exportPdf(ExportType.quantities);
        this._close();
    }

    public _exportAsSmeulders(): void {
        this.exportService.exportPdf(ExportType.smeulders);
        this._close();
    }

    public _exportAsProjects(): void {
        this.exportService.exportPdf(ExportType.projects);
        this._close();
    }

    public _exportAsPga(): void {
        this.exportService.exportPdf(ExportType.pga);
        this._close();
    }

    public _exportAsVariations(): void {
        this.exportService.exportPdf(ExportType.mmw);
        this._close();
    }

    public _openPdfExportModal(): void {
        this.modalService.open(PdfExportComponent);
    }

    /* --- Trello --- */

    public _openTrello(): void {
        window.open('https://trello.com/b/y3MB9Iso/calculatie-tool', '_blank');
    }

    /* --- Changelog --- */

    public async _showChangelog(): Promise<void> {
        // Get changes        
        const changelog = await this.changesService.getChanges();

        // Show changelog
        this.modalService.open(ChangelogModalComponent, { changelog });

        // Close side menu
        this._close();
    }

    /* --- Event handlers --- */

    @HostListener('document:click', ['$event'])
    public async onClick(event: MouseEvent): Promise<void> {
        const menuState = await firstValueFrom(this.store.select(selectSideMenuOpen));
        // Ignore event if closed
        if (!menuState) return;

        // Check whether or not the click even was in the side menu or not
        const clickedChildElement = this.rootElement.nativeElement.contains(event.target as Node);

        // Ignore any clicks in the menu
        if (clickedChildElement) return;

        // console.log('clicked outside side menu', event.target, menuState);

        // Close
        this._close();
    }
}
